// Overview dashboard widget — lists upcoming jobs flagged at weather risk.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CloudRain, MapPin } from "lucide-react";
import api from "@/lib/api";
import WeatherChip from "@/components/WeatherChip";
import { dayShort } from "@/lib/weather";

const fontHead = { fontFamily: "'Barlow Condensed', system-ui, sans-serif" };

export default function WeatherRiskWidget() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api
      .get("/weather/jobs-at-risk")
      .then((r) => mounted && setJobs(r.data?.jobs || []))
      .catch(() => mounted && setJobs([]))
      .finally(() => mounted && setLoading(false));
  }, []);

  if (loading) {
    return (
      <div
        className="rounded-sm border border-zinc-300 dark:border-zinc-800 bg-white dark:bg-[#101620] p-5"
        data-testid="weather-widget-loading"
      >
        <div className="h-4 w-40 bg-zinc-100 dark:bg-[#151C27] animate-pulse" />
      </div>
    );
  }

  // Only show jobs that have any weather risk in next 7 days (amber or red).
  const risky = jobs.filter((j) => j.weather && (j.weather.severity === "red" || j.weather.severity === "amber"));
  if (risky.length === 0) {
    return null; // collapse entirely if nothing is at risk — keep dashboard clean
  }

  const redCount = risky.filter((j) => j.weather.severity === "red").length;
  const amberCount = risky.filter((j) => j.weather.severity === "amber").length;

  return (
    <div
      className="rounded-sm border border-zinc-300 dark:border-zinc-800 bg-white dark:bg-[#101620] overflow-hidden"
      data-testid="weather-widget"
    >
      <div className="px-5 py-3 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-[#151C27] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <CloudRain className="w-3.5 h-3.5 text-sky-500 shrink-0" />
          <div
            className="text-[10px] uppercase tracking-[0.22em] text-sky-500"
            style={{ ...fontHead, fontWeight: 700 }}
          >
            // Weather risk · {risky.length} job{risky.length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {redCount > 0 && (
            <span
              className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.16em] rounded-sm bg-red-600 text-white"
              style={{ ...fontHead, fontWeight: 700 }}
            >
              {redCount} likely wet
            </span>
          )}
          {amberCount > 0 && (
            <span
              className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.16em] rounded-sm bg-amber-400 text-zinc-900"
              style={{ ...fontHead, fontWeight: 700 }}
            >
              {amberCount} showers
            </span>
          )}
        </div>
      </div>
      <div className="max-h-72 overflow-y-auto">
        {risky.slice(0, 10).map((j) => (
          <Link
            key={j.id}
            to={`/app/jobs/${j.id}`}
            className="flex items-center gap-3 px-3 py-2.5 border-b border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-[#151C27] last:border-b-0 transition-colors"
            data-testid={`weather-risk-job-${j.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                {j.job_number && (
                  <span
                    className="text-[10px] uppercase tracking-[0.14em] bg-zinc-900 dark:bg-[#151C27] dark:border dark:border-zinc-700 text-white px-1.5 py-0.5 rounded-sm tabular-nums"
                    style={{ ...fontHead, fontWeight: 700 }}
                  >
                    {j.job_number}
                  </span>
                )}
                <span
                  className="text-[13px] uppercase tracking-[0.04em] text-zinc-900 dark:text-white truncate"
                  style={{ ...fontHead, fontWeight: 800 }}
                >
                  {j.title}
                </span>
              </div>
              <div
                className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 dark:text-zinc-400 mt-0.5 flex items-center gap-2 flex-wrap"
                style={{ ...fontHead, fontWeight: 700 }}
              >
                <span className="tabular-nums">{dayShort(j.start_date)}</span>
                {j.site_address && (
                  <>
                    <span>·</span>
                    <MapPin className="w-3 h-3 shrink-0" />
                    <span className="truncate">{j.site_address}</span>
                  </>
                )}
              </div>
            </div>
            <WeatherChip day={j.weather} compact />
          </Link>
        ))}
      </div>
      {risky.length > 10 && (
        <div
          className="px-5 py-2 text-center text-[10px] uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400 bg-zinc-50 dark:bg-[#151C27]"
          style={{ ...fontHead, fontWeight: 700 }}
        >
          +{risky.length - 10} more — check the Jobs board
        </div>
      )}
      <div className="px-5 py-2 bg-zinc-50 dark:bg-[#151C27] border-t border-zinc-200 dark:border-zinc-800 text-[10px] text-zinc-500 dark:text-zinc-400 italic">
        Forecast from Open-Meteo · updated hourly · NZ thresholds (10mm/70%/60kmh = wet, 3mm/40%/40kmh = showers/windy).
      </div>
    </div>
  );
}
